// ============================================================
// Section Box (Statistiques) : fonctions pures, sans I/O ni store
// ============================================================
// Données du satellite (route /api/stats/economy/box) : journal des ouvertures de PremiumMod 0.0.80 (box avec clé,
// lootbox sans clé) et poids des lots de chaque box (taux prévus). Les clés et box vendues à la boutique sont dans la
// section Boutique (shopStats).

import type { BoxLotDTO } from '@/services/api.service';

/** Version du satellite qui sert la section : la page le dit face à un satellite plus ancien. */
export const BOX_SATELLITE_VERSION = '1.0.30';

export const RARITY_ORDER = ['common', 'rare', 'epic', 'legendary'] as const;

const RARITY_LABELS: Record<string, string> = {
  common: 'Commun',
  rare: 'Rare',
  epic: 'Épique',
  legendary: 'Légendaire',
};

export function rarityLabel(rarity: string): string {
  return RARITY_LABELS[rarity] ?? rarity;
}

/** Couleur d'une rareté, prise dans les teintes validées : gris, bleu, violet, or. */
export function rarityColor(index: number): string {
  return ['var(--job-other)', 'var(--job-1)', 'var(--job-7)', 'var(--job-4)'][index] ?? 'var(--job-other)';
}

/** Couleur d'une box : elle suit la box (son rang dans l'ordre alphabétique des identifiants), pas son classement. */
export function boxColor(box: string, all: readonly string[]): string {
  const sorted = [...new Set(all)].sort();
  const i = sorted.indexOf(box);
  const palette = [1, 2, 3, 5, 6, 8, 4, 7];
  return i >= 0 && i < palette.length ? `var(--job-${palette[i]})` : 'var(--job-other)';
}

/** Nom d'une box : celui de son fichier de langue, sinon son identifiant lisible (« Amethyst lootbox »). */
export function boxLabel(box: string, name?: string | null): string {
  if (name && name.trim()) return name.trim();
  const path = box.includes(':') ? box.slice(box.indexOf(':') + 1) : box;
  const words = path.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : box;
}

/** « paladium:endium_nugget » devient « Endium nugget (paladium) » ; minecraft: est sous-entendu (comme les métiers). */
export function itemLabel(item: string): string {
  const [ns, raw] = item.includes(':') ? item.split(':', 2) as [string, string] : ['minecraft', item];
  const name = raw.replace(/_/g, ' ');
  const pretty = name.charAt(0).toUpperCase() + name.slice(1);
  return ns === 'minecraft' ? pretty : `${pretty} (${ns})`;
}

/** Ce que donne un lot, en mots : « Pépite d'endium x1 », « 2 000 $ », « 8 niveaux », « 150 PB saisonniers ». */
export function lotLabel(lot: Pick<BoxLotDTO, 'item' | 'amount' | 'customName' | 'money' | 'experience' | 'storePoints' | 'seasonal' | 'lot'>): string {
  const parts: string[] = [];
  if (lot.item) {
    const name = lot.customName?.trim() || itemLabel(lot.item);
    parts.push(lot.amount > 1 ? `${name} x${lot.amount.toLocaleString('fr-FR')}` : name);
  }
  if (lot.money) parts.push(`${lot.money.toLocaleString('fr-FR')} $`);
  if (lot.experience) parts.push(`${lot.experience.toLocaleString('fr-FR')} niveau${Math.abs(lot.experience) > 1 ? 'x' : ''}`);
  if (lot.storePoints) parts.push(`${lot.storePoints.toLocaleString('fr-FR')} PB${lot.seasonal ? ' saisonniers' : ''}`);
  return parts.length > 0 ? parts.join(' + ') : lot.lot;
}

/** Taux en pour cent, assez précis pour les lots rares : « 0,31 % », « 4 % », « 53,3 % », « < 0,01 % ». */
export function rateLabel(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return '?';
  const pct = rate * 100;
  if (pct === 0) return '0 %';
  if (pct < 0.01) return '< 0,01 %';
  const digits = pct < 1 ? 2 : pct < 10 ? 1 : pct < 100 ? 1 : 0;
  return `${pct.toLocaleString('fr-FR', { maximumFractionDigits: digits })} %`;
}

export type Verdict = { label: string; tone: 'ok' | 'up' | 'down' };

/**
 * Ce qui est sorti comparé à ce qu'on attendait d'après les poids : « dans la norme » tant que l'écart reste sous
 * deux fois l'écart du hasard (racine du nombre attendu), sinon plus ou moins que prévu. Rien sans attendu, ni
 * quand trop peu de lots sont attendus pour juger (moins de 1).
 */
export function verdict(seen: number, expected: number | null | undefined): Verdict | null {
  if (expected == null || !Number.isFinite(expected) || expected < 1) return null;
  const margin = 2 * Math.sqrt(expected);
  if (Math.abs(seen - expected) <= margin) return { label: 'Dans la norme', tone: 'ok' };
  return seen > expected ? { label: 'Plus que prévu', tone: 'up' } : { label: 'Moins que prévu', tone: 'down' };
}

/** Nombre attendu, avec une décimale sous 10 : « 1,8 », « 41 ». */
export function expectedLabel(expected: number | null | undefined): string {
  if (expected == null || !Number.isFinite(expected)) return '?';
  return expected.toLocaleString('fr-FR', { maximumFractionDigits: expected < 10 ? 1 : 0 });
}
